import type { Ref } from 'vue';
import { computed } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Field } from '../types/field';
import { getSubtotalTax } from '../helpers/pricing-table/pricing-table-price-summary.helper';
import { PricingTableHelper } from '../helpers/pricing-table.helper';
import type { PricingTable } from '../types/models';
import type { EditorProductProperty } from '../types/editor-product-property';
import { usePricingTableColumns } from './pricing-table-columns.composable';

export function usePricingTableSectionSummary(
  pricingTable: Ref<PricingTable>,
  sectionId: Ref<string>,
  fields: Ref<Field[]>,
  productProperties: Ref<EditorProductProperty[]>
) {
  const section = computed(() =>
    pricingTable.value.pricingTableSections.find(({ id }) => id === sectionId.value)
  );

  const isPreCalculated = computed(() => pricingTable.value.preCalculated);

  const { discountColumn, totalPriceColumn, taxColumn } = usePricingTableColumns(
    section,
    isPreCalculated,
    productProperties
  );

  const sectionSummary = computed(() =>
    PricingTableHelper.getSectionSummary(
      section.value,
      fields.value,
      pricingTable.value.preCalculated
    )
  );

  const hasValue = (value: string) => !!value && value !== '0';

  const showDiscount = computed(
    () =>
      discountColumn.value.enabled &&
      totalPriceColumn.value.enabled &&
      (isPreCalculated.value || hasValue(sectionSummary.value.discount?.value))
  );

  const showTax = computed(
    () =>
      taxColumn.value.enabled &&
      (isPreCalculated.value || hasValue(sectionSummary.value.tax?.value))
  );

  const discountValueText = computed(() =>
    hasValue(sectionSummary.value.discount.value)
      ? `-${PricingTableHelper.currencyFormat(sectionSummary.value.discount.value, pricingTable.value.currencySettings)}`
      : t('add-discount')
  );

  const taxValueText = computed(() =>
    PricingTableHelper.currencyFormat(
      getSubtotalTax(sectionSummary.value),
      pricingTable.value.currencySettings
    )
  );

  const subTotalValueText = computed(() =>
    PricingTableHelper.currencyFormat(
      sectionSummary.value.price.value,
      pricingTable.value.currencySettings
    )
  );

  return {
    sectionSummary,
    showDiscount,
    showTax,
    discountValueText,
    taxValueText,
    subTotalValueText,
  };
}
